import { useState } from 'react';
import { Upload, Image as ImageIcon, Link, X, Loader2, Search } from 'lucide-react';
import { useImageUpload } from './useImageUpload';
import { isConfigured as isUnsplashConfigured } from '../../../services/unsplashService';
import { UnsplashPicker } from '../UnsplashPicker';
import type { ImageAttribution } from './shared/UnsplashCredit';

export type { ImageAttribution };

interface ImageDropZoneProps {
  onImageUrl: (url: string, attribution?: ImageAttribution) => void;
  currentUrl?: string;
  compact?: boolean;
  primaryColor?: string;
  className?: string;
}

export function ImageDropZone({
  onImageUrl,
  currentUrl,
  compact = false,
  primaryColor = 'var(--accent, #3b82f6)',
  className = '',
}: ImageDropZoneProps) {
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [showUnsplash, setShowUnsplash] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const unsplashAvailable = isUnsplashConfigured();

  const {
    isUploading,
    error,
    isDragging,
    clearError,
    openFilePicker,
    fileInputRef,
    onFileInputChange,
    dragHandlers,
  } = useImageUpload({ onUploaded: onImageUrl });

  const handleUrlSubmit = () => {
    const trimmed = urlInput.trim();
    if (trimmed) {
      onImageUrl(trimmed);
      setShowUrlInput(false);
      setUrlInput('');
    }
  };

  // Hidden file input used by both modes
  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      onChange={onFileInputChange}
      className="hidden"
    />
  );

  // Compact mode for settings drawers
  if (compact) {
    return (
      <div className={className}>
        {fileInput}

        {/* Thumbnail preview if image exists */}
        {currentUrl && (
          <div className="mb-2 relative rounded-lg overflow-hidden border border-gray-200">
            <img
              src={currentUrl}
              alt=""
              className="w-full h-24 object-cover"
            />
          </div>
        )}

        {/* Drop zone */}
        <div
          {...dragHandlers}
          onClick={openFilePicker}
          className={`relative border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-all ${
            isDragging ? 'scale-[1.02]' : 'hover:bg-gray-50'
          }`}
          style={
            isDragging
              ? { borderColor: 'var(--accent, #3b82f6)', background: 'var(--accent-soft, #eff6ff)' }
              : { borderColor: 'var(--line, #d1d5db)' }
          }
        >
          {isUploading ? (
            <div className="flex items-center justify-center gap-2 py-1" style={{ color: 'var(--accent, #2563eb)' }}>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-xs font-medium">Uploading...</span>
            </div>
          ) : isDragging ? (
            <div className="flex items-center justify-center gap-2 py-1" style={{ color: 'var(--accent, #2563eb)' }}>
              <Upload className="w-4 h-4" />
              <span className="text-xs font-medium">Drop image here</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 py-1">
              <Upload className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-500">
                {currentUrl ? 'Drop to replace or click to browse' : 'Drag image or click to browse'}
              </span>
            </div>
          )}
        </div>

        {error && (
          <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
            {error}
            <button onClick={clearError} className="ml-auto text-red-400 hover:text-red-600">
              <X className="w-3 h-3" />
            </button>
          </p>
        )}

        {/* URL toggle */}
        {showUrlInput ? (
          <div className="mt-2 flex gap-1.5">
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="flex-1 px-2.5 py-1.5 text-xs border rounded-lg focus:outline-none"
              style={{ borderColor: 'var(--line, #d1d5db)' }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent, #3b82f6)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--line, #d1d5db)'; }}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleUrlSubmit();
                if (e.key === 'Escape') { setShowUrlInput(false); setUrlInput(''); }
              }}
            />
            <button
              onClick={handleUrlSubmit}
              className="px-2.5 py-1.5 text-white rounded-lg text-xs"
              style={{ background: 'var(--accent, #2563eb)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--accent-ink, var(--accent, #1d4ed8))'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--accent, #2563eb)'; }}
            >
              Add
            </button>
            <button
              onClick={() => { setShowUrlInput(false); setUrlInput(''); }}
              className="p-1.5 text-gray-400 hover:text-gray-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : showUnsplash ? (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-500">Search Unsplash</span>
              <button
                onClick={() => setShowUnsplash(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <UnsplashPicker
              onSelect={(photo) => {
                onImageUrl(photo.url, {
                  unsplashId: photo.unsplashId,
                  unsplashAuthor: photo.unsplashAuthor,
                  unsplashAuthorUrl: photo.unsplashAuthorUrl,
                });
                setShowUnsplash(false);
              }}
            />
          </div>
        ) : (
          <div className="mt-1.5 flex items-center gap-2">
            <button
              onClick={() => setShowUrlInput(true)}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
            >
              <Link className="w-3 h-3" />
              Paste URL
            </button>
            {unsplashAvailable && (
              <>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => setShowUnsplash(true)}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
                >
                  <Search className="w-3 h-3" />
                  Unsplash
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // Full-size mode for empty states on canvas
  return (
    <div
      {...dragHandlers}
      // pt-14 clears the canvas fullscreen button (34px at top:12) that
      // occupies the same corner when this renders on the right half.
      className={`relative flex flex-col items-center justify-center h-full w-full px-6 pb-6 pt-14 ${className}`}
    >
      {fileInput}

      {/* Drag overlay */}
      {isDragging && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center border-2 border-dashed rounded-xl"
          style={{ background: 'color-mix(in srgb, var(--accent, #3b82f6) 8%, transparent)', borderColor: 'var(--accent, #3b82f6)' }}
        >
          <div className="flex flex-col items-center gap-2" style={{ color: 'var(--accent, #2563eb)' }}>
            <Upload className="w-10 h-10" />
            <span className="text-sm font-medium">Drop image here</span>
          </div>
        </div>
      )}

      {/* Upload spinner */}
      {isUploading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 rounded-xl">
          <div className="flex flex-col items-center gap-3" style={{ color: 'var(--accent, #2563eb)' }}>
            <Loader2 className="w-8 h-8 animate-spin" />
            <span className="text-sm font-medium">Uploading...</span>
          </div>
        </div>
      )}

      {/* Empty state content */}
      {!isDragging && !isUploading && (
        <div
          className="flex flex-col items-center rounded-xl cursor-pointer"
          onClick={openFilePicker}
          style={{
            padding: '24px 28px',
            maxWidth: '100%',
            background: 'color-mix(in srgb, var(--surface-2, #FAFAF9) 94%, transparent)',
            border: '1px dashed var(--line-2, #C8C8C5)',
            transition: 'border-color 120ms',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent, #C8C8C5)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--line-2, #C8C8C5)'; }}
        >
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
            style={{ backgroundColor: `color-mix(in srgb, ${primaryColor} 10%, transparent)` }}
          >
            <ImageIcon className="w-5 h-5" style={{ color: primaryColor }} />
          </div>
          <p style={{ fontSize: 13.5, fontWeight: 500, margin: '0 0 2px', color: 'var(--ink, #374151)' }}>Drag image here</p>
          <p style={{ fontSize: 12, margin: '0 0 14px', color: 'var(--ink-3, #9ca3af)' }}>or click to browse</p>

          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={openFilePicker}
              className="inline-flex items-center whitespace-nowrap"
              style={{ height: 32, padding: '0 14px', gap: 6, fontSize: 12.5, fontWeight: 500, borderRadius: 8, border: '1px solid transparent', background: primaryColor, color: '#fff', cursor: 'pointer', transition: 'background 120ms' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--accent-ink, color-mix(in srgb, var(--accent, #2563eb) 85%, black))'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = primaryColor; }}
            >
              <Upload className="w-3.5 h-3.5" />
              Upload
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setShowUrlInput(true); }}
              className="inline-flex items-center whitespace-nowrap"
              style={{ height: 32, padding: '0 14px', gap: 6, fontSize: 12.5, fontWeight: 500, borderRadius: 8, border: '1px solid var(--line, #d1d5db)', background: 'var(--surface, #fff)', color: 'var(--ink-2, #4b5563)', cursor: 'pointer', transition: 'border-color 120ms, color 120ms' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--line-2, #9ca3af)'; e.currentTarget.style.color = 'var(--ink, #111)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--line, #d1d5db)'; e.currentTarget.style.color = 'var(--ink-2, #4b5563)'; }}
            >
              <Link className="w-3.5 h-3.5" />
              Paste URL
            </button>
            {unsplashAvailable && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowUnsplash(true); }}
                className="inline-flex items-center whitespace-nowrap"
                style={{ height: 32, padding: '0 14px', gap: 6, fontSize: 12.5, fontWeight: 500, borderRadius: 8, border: '1px solid var(--line, #d1d5db)', background: 'var(--surface, #fff)', color: 'var(--ink-2, #4b5563)', cursor: 'pointer', transition: 'border-color 120ms, color 120ms' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--line-2, #9ca3af)'; e.currentTarget.style.color = 'var(--ink, #111)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--line, #d1d5db)'; e.currentTarget.style.color = 'var(--ink-2, #4b5563)'; }}
              >
                <Search className="w-3.5 h-3.5" />
                Unsplash
              </button>
            )}
          </div>

          {showUrlInput && (
            <div className="mt-3 flex gap-2 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none"
                style={{ borderColor: 'var(--line, #d1d5db)', background: 'var(--surface, #fff)', color: 'var(--ink, #111)' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent, #3b82f6)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--line, #d1d5db)'; }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleUrlSubmit();
                  if (e.key === 'Escape') { setShowUrlInput(false); setUrlInput(''); }
                }}
              />
              <button
                onClick={handleUrlSubmit}
                className="px-3 py-2 text-white rounded-lg text-sm"
                style={{ background: 'var(--accent, #2563eb)' }}
              >
                Add
              </button>
              <button
                onClick={() => { setShowUrlInput(false); setUrlInput(''); }}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {showUnsplash && (
            <div className="mt-3 w-full max-w-md rounded-xl shadow-lg p-4" onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface-2, #fff)', border: '1px solid var(--line, #e5e7eb)' }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Search Unsplash</span>
                <button
                  onClick={() => setShowUnsplash(false)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <UnsplashPicker
                onSelect={(photo) => {
                  onImageUrl(photo.url, {
                    unsplashId: photo.unsplashId,
                    unsplashAuthor: photo.unsplashAuthor,
                    unsplashAuthorUrl: photo.unsplashAuthorUrl,
                  });
                  setShowUnsplash(false);
                }}
              />
            </div>
          )}

          {error && (
            <p className="mt-3 text-sm text-red-600">{error}</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Overlay component for drag-to-replace on slides that already have an image.
 * Wrap the image area with this and spread dragHandlers onto the container.
 */
export function ImageReplaceOverlay({
  isDragging,
  isUploading,
}: {
  isDragging: boolean;
  isUploading: boolean;
}) {
  if (!isDragging && !isUploading) return null;

  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center transition-all">
      {isUploading ? (
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
          <div className="flex flex-col items-center gap-2 bg-white/90 rounded-xl px-6 py-4 shadow-lg">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--accent, #2563eb)' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--accent, #1d4ed8)' }}>Uploading...</span>
          </div>
        </div>
      ) : (
        <div
          className="absolute inset-0 border-2 border-dashed flex items-center justify-center"
          style={{ background: 'color-mix(in srgb, var(--accent, #3b82f6) 14%, transparent)', borderColor: 'var(--accent, #60a5fa)' }}
        >
          <div className="flex flex-col items-center gap-2 bg-white/90 rounded-xl px-6 py-4 shadow-lg" style={{ color: 'var(--accent, #2563eb)' }}>
            <Upload className="w-6 h-6" />
            <span className="text-sm font-medium">Drop to replace</span>
          </div>
        </div>
      )}
    </div>
  );
}
